import {Fragment, React} from '@api'
import {usePdfStore} from '@modules/pdf'
import {styled} from '@pdf'
import {EsAggregation, EsTotal} from '@stores/elasticsearch'
import {VlpSpecCardPdf, ProductPageFooter, ProductPageHeader, ProductPerRow} from './'
import {useCatalog} from '@stores'
import {ProductPageStyledComponents} from './VlpProductPage.styles'

export interface IGroupByPage {
  rows?: Array<IGroupByPageRow>
  columns?: Array<IGroupByPageRow>
  pageIndex: number
  rowsPerPage?: number
  groupName?: string,
  complete?: boolean
  count?: number
}

export type ISpecGroupByPdf = EsAggregation & { limited?: number, count?:EsTotal, title?: string, subtitle?: string, hex?: string, groupName?: string, children?: Array<ISpecGroupByPdf> }

export interface IGroupByPageRow {
  spec?: ISpecGroupByPdf
  count: EsTotal
  totalCount?: number
  items: Array<{}>
  columnSize?: number
  rowSize?: number
  rowKey?: string
  rowTitle?: string
}

const GroupByPageContents = styled.View`
  flex-direction: column;
  margin-top:2.3mm;
  flex-grow:1;
`
const VerticalSeparator = styled.View`
  content: "";
  background-color: #808080;
  position: absolute;
  width: 1px;
  display: block;
  top: 0;
  bottom: 25px;
  margin-left: 1px;
  left: ${props => props.width + Math.floor(props.margin / 2)}px;
`
const HorizontalSeparator = styled.View`
  background-color: #808080;;
  text-align: left;
  width: 100%;
  height: 1px;
  margin: 0;
  margin-top: ${p => p.marginTop ?? 0}px;
`
export const GroupByPdfPage: React.FunctionComponent<{ pageInfo: IGroupByPage, download?: boolean }>
  = (props) => {
  const {config: {showVtGroupingLines, showHrGroupingLines, imageRatio, columns}, imageHeight, cardWidth, cardMargin, selectedPdfDocument: {orientation, size}} = usePdfStore()
  const {pageInfo: {rows, columns: infoColumns, pageIndex, complete, count, rowsPerPage}, download} = props,
    {MyReportPage} = ProductPageStyledComponents()

  return (
    <MyReportPage size={size} orientation={orientation} hideTitle wrap={false}>
      <ProductPageHeader/>
      <GroupByPageContents>
        {infoColumns.length > 0 ? rows.map((row, i) => {
          const column: Array<IGroupByPageRow> = infoColumns.filter(c => c.rowKey === row.spec.key)

          return <GroupByColumn
            showGroupingSeparators={{hr: showHrGroupingLines, vt: showVtGroupingLines}}
            rowsPerPage={rowsPerPage}
            row={row}
            showSpec={i == 0 || rows[i - 1]?.spec.key != row.spec.key}
            key={`${row.spec.key}_${pageIndex}_${column[0].rowTitle}_${column[0].spec.key}`}
            column={column}
            index={i}
            imageRatio={imageRatio}
            columns={columns}
            download={download}
          />
        }) : rows.map((row, j) => <GroupByRow
          showGroupingSeparators={{hr: showHrGroupingLines, vt: showVtGroupingLines}}
          row={row}
          showSpec={j == 0 || rows[j - 1]?.spec.key != row.spec.key}
          key={`${row.spec.key}_${pageIndex}-${j}`}
          column={infoColumns}
          index={j}
          imageRatio={imageRatio}
          columns={columns}
          download={download}
          height={imageHeight}
        />)}

        {
          showVtGroupingLines ? <VerticalSeparator margin={cardMargin} width={cardWidth} columns={columns}/> : null
        }
      </GroupByPageContents>
      <ProductPageFooter index={pageIndex - 1} download={download}/>
    </MyReportPage>
  )
}

const GroupByRowView = styled.View`
  display:flex;
  flex-direction: row;
  justify-items: flex-start;
  align-items: flex-start;
  width: 100%;
  max-height:${p => p.cardHeight}px;
`

const GroupByColumnView = styled.View`
  display:flex;
  flex-direction: row;
  justify-items: flex-start;
  align-items: flex-start;
  width: 100%;
`

const SpecColumn = styled.View`
  flex-direction: row;
  width: ${props => props.width}px;
  min-width: ${props => props.width}px;
  align-items: flex-start;
  justify-self: flex-start;
  margin: 0px ${props => (props.margin / 2)}px 0px 0px;
  padding-top:5px;
  max-height: 40%;
`

const ProductsColumn = styled.View`
  flex-direction: row;
  align-items: flex-start;
`

const TwoColumnContainer = styled.View`
  display: flex;
  flex-direction: row;
  height: ${p => p.cardHeight * p.rowSize};
  width: 100%;
  flex-wrap: wrap;
`

const CatalogColumn = styled.View`
  display: flex;
  flex-direction: column;
  width: ${props => props.columnsPerPage}px;
`
const SpecColumnRow = styled.View`
  margin-top: 5px;
  padding: 0px;
`
const CatalogColumnRow = styled.View`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
  height: 90%;
`

export const GroupByColumn: React.FC<{ style?, column?: Array<any>, row: IGroupByPageRow, index: number, showSpec: boolean, rowsPerPage?: number, imageRatio?, columns?, download?: boolean, showGroupingSeparators?: any }>
  = ({showSpec, rowsPerPage, column, row: {spec}, index, imageRatio, columns, download, showGroupingSeparators}) => {
  const {columnsPage, cardWidth, cardHeight, imageHeight, cardMargin, horizontalMargin} = usePdfStore(),
    catalog = useCatalog(),
    {groupBy: {hierarchyGroups}} = catalog

  return (<Fragment>
      {showSpec && showGroupingSeparators.hr ? <HorizontalSeparator marginTop={index === 1 ? horizontalMargin : 0} /> : null}
      <GroupByColumnView rowsPerPage={rowsPerPage}>
        <SpecColumn width={cardWidth} margin={cardMargin} imageRatio={imageRatio} columns={columns}>
          {!showSpec ? null : <VlpSpecCardPdf height={imageHeight} spec={spec}/>}
        </SpecColumn>
        {column.length > 0 && <TwoColumnContainer cardHeight={cardHeight} rowSize={Math.max(...column.map(c => c.rowSize))}>
          {column.length > 0 && column.map((p, c) => <CatalogColumn columnsPerPage={columnsPage(p.columnSize)} key={c}>
              {p.items.filter(i => i.spec).map((i, n) => <SpecColumnRow key={n}><ProductPerRow group={hierarchyGroups[1]} width={column.reduce((acc, c) => (acc += c.spec?.title === i.title ? c.columnSize : 0), 0) * (cardWidth + (cardMargin / 2))} onlyTitle download={download} product={i} columnSize={p.columnSize}/></SpecColumnRow>)}
              <CatalogColumnRow style={{width: p.items.filter(i => !i.spec).length > 1 ? 'auto' : '50%'}}>{p.items.filter(i => !i.spec).map((i, c) =>
                <ProductPerRow key={c} download={download} product={i}/>
              )}
              </CatalogColumnRow>
            </CatalogColumn>
          )}
		</TwoColumnContainer>}
      </GroupByColumnView>
    </Fragment>
  )
}

export const GroupByRow: React.FunctionComponent<{ style?, column?: Array<any>, row: IGroupByPageRow, index: number, showSpec: boolean, imageRatio?, columns?, download?: boolean, showGroupingSeparators?: any, height?: number }>
  = function GroupByRow({showSpec, column, row: {items, spec}, imageRatio, columns, download, showGroupingSeparators, height}) {
  const {cardWidth, cardHeight, cardMargin, imageHeight} = usePdfStore(),
    catalog = useCatalog(),
    {groupBy: {hierarchyGroups}} = catalog

  return (<Fragment>
      {showSpec && showGroupingSeparators.hr ? <HorizontalSeparator/> : null}
      <GroupByRowView cardHeight={cardHeight}>
        <SpecColumn width={cardWidth} margin={cardMargin} imageRatio={imageRatio} columns={columns}>
          {
            !showSpec ? null : <VlpSpecCardPdf height={height} spec={spec} />}
          {/*<Text style={{textTransform: 'uppercase', fontSize: 9, paddingLeft: 4, paddingTop: 4, paddingBottom: 4}}>Unassigned</Text>*/}

        </SpecColumn>
        {column.length === 0 && <ProductsColumn>
          {items.length > 0 && items.map((p: any, c) => p.spec ? <SpecColumnRow key={catalog.idFor(p.spec)}><ProductPerRow height={imageHeight} group={hierarchyGroups[1]} download={download} product={p}/></SpecColumnRow> : <ProductPerRow download={download} product={p} key={catalog.idFor(p)}/>)}
		</ProductsColumn>}
      </GroupByRowView>
    </Fragment>
  )
}


// WEBPACK FOOTER //
// ./src/modules/pdf/templates/vlp/GroupByPdfPage.tsx